// Zentraler Abruf der Google-OAuth-Client-ID (GET google-oauth-handler).
//
// Warum das ein eigenes Modul ist (Sentry DENO-T, 2026-08-17): der GET ist der
// erste Schritt JEDES OAuth-Flows (Login, Merchant-Connect, Reauth, Onboarding,
// Ads-Connect). Waehrend einer Edge-Runtime-Bootwelle (Worker-Pool-Churn)
// beantwortete die Plattform den CORS-Preflight mit 502/503 — der Ads-Connect
// scheiterte damit zweimal in Folge, bevor er ueberhaupt begann. Die Client-ID
// ist aber (a) oeffentlich (sie steht in jeder OAuth-URL im Browser) und
// (b) praktisch unveraenderlich. Deshalb:
//   1. Netzwerk zuerst, mit kurzer Retry-Kette — der Server bleibt Quelle der
//      Wahrheit, eine rotierte Client-ID greift beim naechsten Erfolg sofort.
//   2. Bei Totalausfall: zuletzt erfolgreich geholte ID aus localStorage.
//   3. Nur wenn Netz UND Cache leer sind, wirft der Helper — mit EXAKT der
//      Message 'Failed to get Google Client ID', damit Sentry-Issue DENO-T
//      Restfaelle weiter in derselben Gruppe sammelt.

import { supabase } from '@/integrations/supabase/client';

export const GOOGLE_CLIENT_ID_STORAGE_KEY = 'fo_google_oauth_client_id';

// 2 Retries decken den beobachteten Fall (einzelner 502-Preflight) ab, ohne den
// Nutzer bei echtem Ausfall laenger als ~2s vor einem toten Button warten zu lassen.
const RETRY_DELAYS_MS = [500, 1500];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function readCache(): string | null {
  try {
    const v = window.localStorage.getItem(GOOGLE_CLIENT_ID_STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null; // localStorage gesperrt (z.B. Privatmodus) — dann gibt es eben keinen Fallback
  }
}

function writeCache(clientId: string): void {
  try {
    window.localStorage.setItem(GOOGLE_CLIENT_ID_STORAGE_KEY, clientId);
  } catch {
    // Cache ist Komfort, kein Vertrag.
  }
}

/**
 * Holt die Google-OAuth-Client-ID mit Retries; faellt bei Totalausfall auf die
 * zuletzt erfolgreich geholte ID zurueck. Wirft nur, wenn Netz UND Cache leer sind.
 */
export async function getGoogleClientId(): Promise<string> {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1]);
    const { data, error } = await supabase.functions.invoke('google-oauth-handler', { method: 'GET' });
    const clientId = (data as { clientId?: string } | null)?.clientId;
    if (!error && typeof clientId === 'string' && clientId.length > 0) {
      writeCache(clientId);
      return clientId;
    }
  }
  const cached = readCache();
  if (cached) return cached;
  throw new Error('Failed to get Google Client ID');
}
